import { Button, Card, Field, FieldLabel, Textarea } from "@peer-start/ui/reducate";
import { ChevronRight, GraduationCap, LibraryBig, MessagesSquare, Speech } from "lucide-react";
import { useTranslation } from "react-i18next";
import badgeCheck from "../../../assets/starting-teacher/badge-check.svg?url";
import { startingTeacherCourseUrl, type SkillRef, type StartingTeacherTaskId } from "../data/starting-teacher-route.js";

const rows = [
  {
    id: "course",
    icon: GraduationCap,
    tone: "bg-primary/10 text-primary",
    variant: "primary",
    controlId: "starting-teacher.course.start",
  },
  {
    id: "learn",
    icon: LibraryBig,
    tone: "bg-warning text-warning-foreground",
    variant: "secondary",
    controlId: "starting-teacher.row.learn",
  },
  {
    id: "practice",
    icon: Speech,
    tone: "bg-info text-info-foreground",
    variant: "secondary",
    controlId: "starting-teacher.row.practice",
  },
  {
    id: "chat",
    icon: MessagesSquare,
    tone: "bg-chart-5/15 text-chart-5",
    variant: "secondary",
    controlId: "starting-teacher.row.chat",
  },
] as const;

export function StartingTeacherRecommended({
  skill,
  topicId,
  taskOpen,
  note,
  achievedTasks,
  onStartTask,
  onOpenTask,
  onOpenSources,
  onOpenChat,
  onNote,
  onSaveNote,
}: {
  skill: SkillRef;
  topicId: string;
  taskOpen: boolean;
  note: string;
  achievedTasks: Partial<Record<StartingTeacherTaskId, true>> | undefined;
  onStartTask: (taskId: StartingTeacherTaskId) => void;
  onOpenTask: () => void;
  onOpenSources: () => void;
  onOpenChat: () => void;
  onNote: (note: string) => void;
  onSaveNote: () => void;
}) {
  const { t } = useTranslation();
  const openRow = (row: (typeof rows)[number]["id"]) => {
    onStartTask(row);
    if (row === "practice") onOpenTask();
    if (row === "learn") onOpenSources();
    if (row === "chat") onOpenChat();
  };

  return (
    <Card data-control-id="starting-teacher.actions">
      <h2 className="text-lg font-medium">{t("startingTeacher.tasks")}</h2>
      <p className="mt-1.5 text-sm leading-5 text-secondary-text">{t("startingTeacher.tasksIntro")}</p>
      <ul className="mt-5 space-y-3">
        {rows.map((row) => {
          const Icon = row.icon;
          const achieved = Boolean(achievedTasks?.[row.id]);
          const title =
            row.id === "course"
              ? t("startingTeacher.rows.course.title")
              : t(`startingTeacher.rows.${row.id}.title`);
          const hint =
            row.id === "course"
              ? t(`startingTeacher.skills.${skill.id}.course`)
              : t(`startingTeacher.rows.${row.id}.hint`);
          const summary = (
            <>
              {achieved ? (
                <img alt="" className="size-8 shrink-0" height={32} src={badgeCheck} width={32} />
              ) : (
                <span
                  aria-hidden="true"
                  className={`grid size-9 shrink-0 place-items-center rounded-lg ${row.tone}`}
                >
                  <Icon className="size-5" strokeWidth={1.75} />
                </span>
              )}
              <span className="min-w-0 flex-1 text-left">
                <span className="block truncate text-sm font-medium">{title}</span>
                <span className="mt-1 block text-xs leading-[18px] text-secondary-text">{hint}</span>
              </span>
            </>
          );
          const chevron = <ChevronRight aria-hidden="true" className="size-6 shrink-0 text-muted-foreground" />;
          const rowClass =
            "flex w-full items-center gap-4 rounded-card border border-border bg-background p-4 text-left focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30";
          return (
            <li key={row.id}>
              {achieved ? (
                row.id === "course" ? (
                  <a
                    aria-label={title}
                    className={rowClass}
                    data-control-id={row.controlId}
                    href={startingTeacherCourseUrl}
                    onClick={() => onStartTask("course")}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {summary}
                    {chevron}
                  </a>
                ) : (
                  <button
                    aria-label={title}
                    className={rowClass}
                    data-control-id={row.controlId}
                    onClick={() => openRow(row.id)}
                    type="button"
                  >
                    {summary}
                    {chevron}
                  </button>
                )
              ) : (
                <div className={rowClass}>
                  {summary}
                  {row.id === "course" ? (
                    <a
                      aria-label={`${t("startingTeacher.start")}: ${title}`}
                      className="inline-flex h-[32px] shrink-0 items-center justify-center rounded-xl border-0 bg-primary px-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
                      data-control-id={row.controlId}
                      href={startingTeacherCourseUrl}
                      onClick={() => onStartTask("course")}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {t("startingTeacher.start")}
                    </a>
                  ) : (
                    <Button
                      aria-label={`${t("startingTeacher.start")}: ${title}`}
                      controlId={row.controlId}
                      onClick={() => openRow(row.id)}
                      type="button"
                      variant={row.variant}
                    >
                      {t("startingTeacher.start")}
                    </Button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {taskOpen ? (
        <TaskNote note={note} onNote={onNote} onSaveNote={onSaveNote} topicId={topicId} />
      ) : null}
    </Card>
  );
}

function TaskNote({
  topicId,
  note,
  onNote,
  onSaveNote,
}: {
  topicId: string;
  note: string;
  onNote: (note: string) => void;
  onSaveNote: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-4 rounded-card border border-border px-4 py-4">
      <p className="text-sm leading-relaxed">{t(`startingTeacher.topics.${topicId}.task`)}</p>
      <Field className="mt-4">
        <FieldLabel htmlFor="starting-teacher-note">{t("startingTeacher.noteLabel")}</FieldLabel>
        <Textarea
          className="min-h-24"
          data-control-id="starting-teacher.note"
          id="starting-teacher-note"
          onChange={(event) => onNote(event.target.value)}
          value={note}
        />
      </Field>
      <Button
        className="mt-3"
        controlId="starting-teacher.note.save"
        disabled={!note.trim()}
        onClick={onSaveNote}
        type="button"
      >
        {t("startingTeacher.saveNote")}
      </Button>
    </div>
  );
}
